/**
 * useCancelOrder (Batch B.1 PART 3) — wallet-signed cancellation of an in-flight cross-chain order.
 *
 * The /dex/cancel endpoint requires a signature from the order's source_address proving ownership (the
 * shared public API key is NOT proof). This hook produces that signature with the connected wallet of the
 * order's family and submits it:
 *   - EVM:    wagmi signTypedData over the shared ParseCancel EIP-712 domain (chainId-bound)
 *   - Tron:   the adapter's signMessageV2 over the shared canonical string
 *   - Solana: the adapter's signMessage over the shared canonical string, base58-encoded
 *
 * A fresh single-use nonce + 5-minute deadline are generated per attempt. On a 403/expired response the
 * status becomes 'error' so the UI can prompt a re-sign with a new nonce/deadline (the consumed nonce is
 * single-use — a retry MUST start a new attempt). The signed content comes from @parse/shared, so it can
 * never drift from what the backend verifies.
 */
import { useCallback, useState } from 'react'
import { useConfig } from 'wagmi'
import { signTypedData } from '@wagmi/core'
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react'
import { chainFamily } from '@parse/shared'
import { useTronWallet } from './useTronWallet'
import { EVM_CHAIN_IDS } from './useDexBridge'
import { dexApi } from '@/services/api'
import {
  CANCEL_DOMAIN,
  CANCEL_TYPES,
  signCancelTron,
  signCancelSolana,
  type CancelFields,
} from '@/services/cancelAuth'
import type { StoredDexOrder } from '@/utils/dexOrderStorage'
import { getDisplayError } from '@/utils/errors'

export type CancelStatus = 'idle' | 'signing' | 'submitting' | 'success' | 'error'
export interface CancelState {
  status: CancelStatus
  /** Human-readable message for the 'error' state (e.g. the 403/expired re-sign prompt). */
  error?: string
}

/** Codes/messages from the backend that mean "re-sign with a fresh nonce/deadline". */
function isReauthError(code?: string, message?: string): boolean {
  const hay = `${code ?? ''} ${message ?? ''}`.toLowerCase()
  return /unauthor|expired|nonce|deadline|forbidden/.test(hay)
}

export function useCancelOrder() {
  const wagmiConfig = useConfig()
  const { signMessage: solanaSignMessage } = useSolanaWallet()
  const { signTronMessage } = useTronWallet()
  const [cancelStates, setCancelStates] = useState<Record<string, CancelState>>({})

  const setState = useCallback((transferId: string, s: CancelState) => {
    setCancelStates(prev => ({ ...prev, [transferId]: s }))
  }, [])

  // `auth` carries the nonce + deadline minted ONCE when the review modal opened. The signature MUST reuse
  // those exact values — if we re-minted the deadline here, the user would have reviewed an "Expires" they
  // never signed (shown ≠ signed). The review flow is the single source of nonce/deadline.
  const cancelOrder = useCallback(async (
    order: StoredDexOrder,
    auth: { nonce: string; deadline: number },
  ): Promise<void> => {
    const transferId = order.transferId
    const family = chainFamily(order.sourceChain)
    const { nonce, deadline } = auth
    // VERBATIM: `chain` is order.sourceChain — the exact string sent to /bridge and persisted as
    // transfers.source_chain. For Tron/Solana this string is part of the signed canonical message, so it
    // MUST match the backend byte-for-byte — never lowercase it, never swap to a chain_id. (EVM ignores it:
    // the EIP-712 struct carries no chain; only the domain chainId binds the chain.) See backend twin in
    // auto-cancel.ts userCancel + the §9 manual gate.
    const fields: CancelFields = { transferId, wallet: order.sourceAddress, nonce, deadline, chain: order.sourceChain }

    try {
      setState(transferId, { status: 'signing' })
      let signature: string

      if (family === 'evm') {
        const chainId = EVM_CHAIN_IDS[order.sourceChain]
        if (!chainId) throw new Error(`Unsupported source chain: ${order.sourceChain}`)
        // wagmi signs the SAME shared domain+types the backend verifies (chainId-bound).
        signature = await signTypedData(wagmiConfig, {
          account: order.sourceAddress as `0x${string}`,
          domain: { ...CANCEL_DOMAIN, chainId },
          types: CANCEL_TYPES,
          primaryType: 'Cancel',
          message: {
            transferId,
            wallet: order.sourceAddress as `0x${string}`,
            nonce,
            deadline: BigInt(deadline),
          },
        })
      } else if (family === 'tron') {
        if (!signTronMessage) throw new Error('Connect the Tron wallet that owns this order to cancel')
        signature = await signCancelTron(signTronMessage, fields)
      } else if (family === 'solana') {
        if (!solanaSignMessage) throw new Error('Connect the Solana wallet that owns this order to cancel')
        signature = await signCancelSolana(solanaSignMessage, fields)
      } else {
        // family === 'unknown' — a chain label with no signing arm (#459 APPROACH E).
        // EXHAUSTIVE BY CONSTRUCTION: this used to be the Solana arm, so a bitcoin/litecoin order
        // would have told the user to "Connect the Solana wallet that owns this order" — advice
        // that is impossible to follow and hides the real cause. Name the chain and stop.
        // Kept under 100 chars: getDisplayError truncates an UNKNOWN-tag message at 100 (errors.ts:830).
        throw new Error(`Cancel isn't supported for ${order.sourceChain} orders — please contact support`)
      }

      setState(transferId, { status: 'submitting' })
      const res = await dexApi.cancelOrder(transferId, {
        signature,
        wallet: order.sourceAddress,
        nonce,
        deadline,
      })

      if (!res.success) {
        // Since ITEM 3 the backend returns ALL cancel failures as a non-2xx, so fetchApi normalizes them
        // into res.error = { code, message } — the breaker reason (gas ceiling / per-wallet / per-tx cap)
        // arrives in res.error.message and is surfaced to the user. The typeof-string branch is a defensive
        // fallback only (a stray 200-with-flat-string body), no longer the load-bearing path.
        const errAny = res.error as unknown
        const errCode = typeof errAny === 'string' ? errAny : res.error?.code
        const errMsg = typeof errAny === 'string' ? errAny : res.error?.message
        const msg = isReauthError(errCode, errMsg)
          ? 'Authorization failed or expired — try again'
          : (errMsg ? getDisplayError(errMsg) : 'Cancel failed — try again')
        setState(transferId, { status: 'error', error: msg })
        return
      }

      // The backend has initiated the cancel; the order poller advances it to its terminal state. We do
      // NOT mutate the stored order here (it isn't 'failed' yet) — the 'success' state drives the UI.
      setState(transferId, { status: 'success' })
    } catch (e) {
      const raw = (e as Error)?.message || 'Cancel failed'
      // A user-rejected wallet prompt is not an error worth alarming about.
      const friendly = /reject|denied|cancell?ed by user|user rejected/i.test(raw)
        ? 'Signature rejected'
        : getDisplayError(e)
      setState(transferId, { status: 'error', error: friendly })
    }
  }, [wagmiConfig, solanaSignMessage, signTronMessage, setState])

  const resetCancelState = useCallback((transferId: string) => {
    setCancelStates(prev => {
      const next = { ...prev }
      delete next[transferId]
      return next
    })
  }, [])

  return { cancelOrder, cancelStates, resetCancelState }
}
